import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import HomeSetupCard from './HomeSetupCard';

/**
 * Centered Shopify-style Home: welcome, quick jump, setup card grid.
 */
const ProductHome = ({
  brandLabel = 'vendaList',
  entityLabel = 'workspace',
  subtitle = null,
  cards = [],
  quickLinks = [],
  progress = null,
  footer = null,
  checklistVisible = true,
  onDismissChecklist = null,
  onRestoreChecklist = null,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!checklistVisible) return [];
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter(
      (card) =>
        card.title?.toLowerCase().includes(q) ||
        card.description?.toLowerCase().includes(q) ||
        card.cta?.toLowerCase().includes(q)
    );
  }, [cards, query, checklistVisible]);

  const handleQuickSubmit = (e) => {
    e.preventDefault();
    const q = query.trim().toLowerCase();
    if (!q) return;
    const match =
      filtered[0] ||
      quickLinks.find(
        (link) =>
          link.label?.toLowerCase().includes(q) ||
          link.href?.toLowerCase().includes(q)
      );
    if (match?.href) navigate(match.href);
  };

  return (
    <div className="mx-auto max-w-5xl px-1 py-4 sm:py-8">
      <div className="mb-6 text-center sm:mb-8">
        <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-ink-tertiary">
          Welcome to {brandLabel}
        </p>
        <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
          Let’s set up {entityLabel}
        </h1>
        {subtitle ? (
          <p className="mx-auto mt-2 max-w-xl text-xs text-ink-secondary sm:text-sm">
            {subtitle}
          </p>
        ) : null}
        {progress ? (
          <p className="mt-2 text-xs text-ink-tertiary">
            {progress.done} of {progress.total} complete
          </p>
        ) : null}
      </div>

      <form
        onSubmit={handleQuickSubmit}
        className="mx-auto mb-6 max-w-2xl sm:mb-8"
      >
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Help me get started…"
            className="product-input-icon box-border h-10 min-h-10 w-full rounded-full border border-border bg-surface text-xs text-ink shadow-sm placeholder:text-ink-tertiary focus:border-ink focus:outline-none focus:ring-1 focus:ring-ink"
          />
          <button
            type="submit"
            className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-ink text-white hover:bg-neutral-800"
            aria-label="Go"
          >
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </label>
        {quickLinks.length > 0 ? (
          <div className="mt-3 flex flex-wrap justify-center gap-1.5">
            {quickLinks.map((link) => (
              <button
                key={link.href}
                type="button"
                onClick={() => navigate(link.href)}
                className="inline-flex h-7 items-center rounded-md border border-border bg-surface px-2.5 text-[11px] font-medium text-ink-secondary hover:bg-surface-muted hover:text-ink"
              >
                {link.label}
              </button>
            ))}
          </div>
        ) : null}
      </form>

      {checklistVisible ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((card) => (
              <HomeSetupCard key={card.id || card.href || card.title} {...card} />
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="mt-6 text-center text-xs text-ink-secondary">
              No matching setup tasks. Try another search.
            </p>
          ) : null}

          {onDismissChecklist ? (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={onDismissChecklist}
                className="text-[11px] font-medium text-ink-tertiary underline-offset-2 hover:text-ink hover:underline"
              >
                Dismiss checklist
              </button>
            </div>
          ) : null}
        </>
      ) : onRestoreChecklist ? (
        <div className="rounded-lg border border-dashed border-border bg-surface px-4 py-8 text-center">
          <p className="text-xs text-ink-secondary">Setup checklist is hidden.</p>
          <button
            type="button"
            onClick={onRestoreChecklist}
            className="mt-2 text-xs font-medium text-ink underline-offset-2 hover:underline"
          >
            Show checklist again
          </button>
        </div>
      ) : null}

      {footer ? <div className="mt-8">{footer}</div> : null}
    </div>
  );
};

export default ProductHome;
